import { useCallback, useEffect, useRef, useState } from 'react'
import type { Bestand } from '../../../gedeeld/types'
import { c } from '../../lib/api'
import { grootte, MAX_GROOTTE, onthoudPlaatje, soortVan, upload } from '../../lib/bestanden'

/* ===========================================================================
 *  De bijlagen boven het invoerveld
 *
 *  Een bestand gaat METEEN naar de server zodra je het toevoegt, niet pas
 *  bij versturen: dan is het er al als je klaar bent met typen, en zie je
 *  een fout (te groot, geen verbinding) voordat je op Enter drukt.
 *
 *  Wat je weer weghaalt, wordt ook bij de server weggehaald (bestand-weg).
 *  Wat je toevoegde maar nooit verstuurde -- je gaat naar een ander gesprek
 *  of naar de taken -- ook. Een upload hoort bij één vraag en nergens anders.
 * =========================================================================== */

export interface Bijlage {
  sleutel: string
  naam: string
  mime: string
  grootte: number
  /** Een object-URL van het bestand op je eigen computer, alleen bij een afbeelding. */
  voorbeeld: string | null
  /** 0 tot 1. */
  voortgang: number
  bestand: Bestand | null
  fout: string | null
}

let teller = 0

export function useBijlagen(gesprekId: () => string | null) {
  const [lijst, setLijst] = useState<Bijlage[]>([])
  const stoppers = useRef(new Map<string, () => void>())
  const spiegel = useRef<Bijlage[]>([])
  spiegel.current = lijst

  const werk = (sleutel: string, deel: Partial<Bijlage>) =>
    setLijst((x) => x.map((b) => (b.sleutel === sleutel ? { ...b, ...deel } : b)))

  const voegToe = useCallback((bestanden: File[]) => {
    const nieuw: Bijlage[] = []
    for (const f of bestanden) {
      const sleutel = `bijlage-${++teller}`
      const beeld = soortVan(f.name, f.type) === 'beeld' && f.type.startsWith('image/')
      const b: Bijlage = {
        sleutel, naam: f.name || 'bestand', mime: f.type, grootte: f.size,
        voorbeeld: beeld ? URL.createObjectURL(f) : null, voortgang: 0, bestand: null, fout: null,
      }
      /* Te groot of leeg: meteen zeggen, en niet eerst vijftien megabyte
         versturen om dat van de server te horen. */
      if (f.size > MAX_GROOTTE) b.fout = `Te groot (${grootte(f.size)}); het kan tot 15 MB.`
      else if (f.size === 0) b.fout = 'Het bestand is leeg.'
      nieuw.push(b)
      if (b.fout) continue

      const u = upload(f, gesprekId(), (deel) => werk(sleutel, { voortgang: deel }))
      stoppers.current.set(sleutel, u.stop)
      u.klaar
        .then((bestand) => {
          stoppers.current.delete(sleutel)
          if (!spiegel.current.some((x) => x.sleutel === sleutel)) {
            /* Weggehaald terwijl hij nog onderweg was: dan ook bij de server weg. */
            void c('bestand-weg', { id: bestand.id }).catch(() => {})
            return
          }
          werk(sleutel, { bestand, voortgang: 1 })
        })
        .catch((e) => {
          stoppers.current.delete(sleutel)
          werk(sleutel, { fout: e instanceof Error ? e.message : String(e) })
        })
    }
    setLijst((x) => [...x, ...nieuw])
  }, [gesprekId])

  const weg = useCallback((sleutel: string) => {
    const b = spiegel.current.find((x) => x.sleutel === sleutel)
    if (!b) return
    stoppers.current.get(sleutel)?.()
    stoppers.current.delete(sleutel)
    if (b.bestand) void c('bestand-weg', { id: b.bestand.id }).catch(() => {})
    if (b.voorbeeld) URL.revokeObjectURL(b.voorbeeld)
    setLijst((x) => x.filter((y) => y.sleutel !== sleutel))
  }, [])

  /** Alles weg, ook bij de server: bij een ander gesprek of als het scherm sluit. */
  const wisAlles = useCallback(() => {
    for (const b of spiegel.current) {
      stoppers.current.get(b.sleutel)?.()
      if (b.bestand) void c('bestand-weg', { id: b.bestand.id }).catch(() => {})
      if (b.voorbeeld) URL.revokeObjectURL(b.voorbeeld)
    }
    stoppers.current.clear()
    spiegel.current = []
    setLijst([])
  }, [])

  /**
   * Voor versturen: de klare bijlagen eruit, zonder ze bij de server weg te
   * halen. Het plaatje blijft bewaard voor het bericht in dit tabblad.
   */
  const neem = useCallback((): Bijlage[] => {
    const klaar = spiegel.current.filter((b) => b.bestand)
    for (const b of klaar) if (b.voorbeeld && b.bestand) onthoudPlaatje(b.bestand.id, b.voorbeeld)
    spiegel.current = []
    setLijst([])
    return klaar
  }, [])

  /** Versturen mislukte: terug boven het veld. */
  const zetTerug = useCallback((terug: Bijlage[]) => setLijst((x) => [...terug, ...x]), [])

  useEffect(() => () => {
    /* Het scherm gaat dicht met bijlagen die nooit verstuurd zijn. */
    for (const b of spiegel.current) {
      stoppers.current.get(b.sleutel)?.()
      if (b.bestand) void c('bestand-weg', { id: b.bestand.id }).catch(() => {})
    }
  }, [])

  return {
    lijst,
    voegToe,
    weg,
    wisAlles,
    neem,
    zetTerug,
    bezig: lijst.some((b) => !b.bestand && !b.fout),
    fout: lijst.some((b) => b.fout),
    klaar: lijst.filter((b) => b.bestand).length,
  }
}
